/**
 * CheTuo Web — SaaS admin plan orders page.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { listPlanOrders, confirmPlanOrder, type PlanOrder } from '../../api/admin-plan';
import { ApiError } from '../../api/client';

const STATUS_TABS = [
  { label: '全部', value: '' },
  { label: '待确认', value: 'pending' },
  { label: '已确认', value: 'paid' },
  { label: '已取消', value: 'cancelled' },
];

const STATUS_LABELS: Record<string, string> = {
  pending: '待确认',
  paid: '已确认',
  cancelled: '已取消',
};

const CHANNEL_LABELS: Record<string, string> = {
  wechat_jsapi: '微信支付',
  offline_wechat: '线下微信',
  offline_cash: '线下现金',
  offline_bank: '线下银行转账',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export function PlanOrdersPage(): React.JSX.Element {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['plan-orders', status || 'all', page, pageSize],
    queryFn: () => listPlanOrders({ status: status || undefined, page, pageSize }),
  });

  const mutation = useMutation({
    mutationFn: confirmPlanOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-orders'] });
    },
  });

  const handleTab = (value: string) => {
    setStatus(value);
    setPage(1);
  };

  const handlePageSize = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#1E293B]">套餐订单</h1>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleTab(tab.value)}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                status === tab.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#64748B]">每页</span>
          <select
            value={pageSize}
            onChange={(e) => handlePageSize(Number(e.target.value))}
            className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>订单列表 ({data?.total ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {mutation.isError && (
            <p className="mb-4 text-sm text-red-600">
              {mutation.error instanceof ApiError ? mutation.error.message : '确认失败'}
            </p>
          )}
          {isLoading ? (
            <p className="text-[#64748B]">加载中...</p>
          ) : isError ? (
            <p className="text-red-600">{error instanceof ApiError ? error.message : '加载失败'}</p>
          ) : !data?.items.length ? (
            <p className="text-[#64748B]">暂无订单</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-600">
                  <th className="pb-3 pr-4 font-medium">门店</th>
                  <th className="pb-3 pr-4 font-medium">套餐</th>
                  <th className="pb-3 pr-4 font-medium">金额</th>
                  <th className="pb-3 pr-4 font-medium">渠道</th>
                  <th className="pb-3 pr-4 font-medium">状态</th>
                  <th className="pb-3 pr-4 font-medium">创建时间</th>
                  <th className="pb-3 pr-4 font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item: PlanOrder) => (
                  <tr key={item.order.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 pr-4 font-medium text-[#1E293B]">{item.shop.name}</td>
                    <td className="py-3 pr-4 text-gray-700">{item.plan.name}</td>
                    <td className="py-3 pr-4 font-medium text-[#1E293B]">
                      ¥{(item.order.amountCents / 100).toFixed(2)}
                    </td>
                    <td className="py-3 pr-4 text-gray-700">
                      {CHANNEL_LABELS[item.order.channel] ?? item.order.channel}
                    </td>
                    <td className="py-3 pr-4">
                      <span className="rounded px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700">
                        {STATUS_LABELS[item.order.status] ?? item.order.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-[#64748B]">
                      {new Date(item.order.createdAt).toLocaleDateString('zh-CN')}
                    </td>
                    <td className="py-3 pr-4">
                      {item.order.status === 'pending' ? (
                        <Button
                          size="sm"
                          onClick={() => mutation.mutate(item.order.id)}
                          disabled={mutation.isPending}
                        >
                          {mutation.isPending ? '处理中...' : '确认到账'}
                        </Button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data && data.total > data.pageSize && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-[#64748B]">
                第 {data.page} 页，共 {totalPages} 页
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                  上一页
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * data.pageSize >= data.total}
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
